/**
 * One turn for the whole fleet, resolved in time order.
 *
 * Every boat traverses its path over the same turn, so progress along a path is
 * also the fraction of the turn that has passed. That is the shared clock the
 * sweep runs on, and it is why the arc-length table in `path.ts` exists: the
 * hull tween is arc-length uniform, and a sim that sampled the raw cubic would
 * report contacts at instants the player watches the boats miss.
 *
 * A boat that is not moving is a path that stays at one point, so turn-by-turn
 * hot seat is the same code as a simultaneous round, not a special case.
 *
 * The loop is the boat-to-boat twin of `resolveBounceChain`: find the earliest
 * event left in the turn, spend it, re-cast what remains, look again.
 */
import { contactAlongHull, faultFromClosing, type BoatContact } from './boatContact.ts';
import { hullCapsule, hullContact, type HullCapsule, type HullDimensions } from './boatHull.ts';
import { impactBody, resolveBoatImpact, separateAfterImpact } from './boatImpact.ts';
import type { CollisionContact } from '../../collision.ts';
import { CRASH_HARSHNESS, MIN_SPEED, TURN_TAX } from '../../constants.ts';
import { driveLevel } from '../../crashCost.ts';
import { BOAT_CONTACT_MAX, BOAT_SWEEP_REFINEMENTS, BOAT_SWEEP_STEPS } from './constants.ts';
import { classifyBoatHit, penalizeAtFault, worseSeverity } from './penalty.ts';
import type { CrashImpact } from '../../crash.ts';
import type { BoatHitSeverity, FleetRun, FleetSeat } from '../host.ts';
import type { MapDocument } from '../../map/types.ts';
import { add, clamp, dot, fromAngle, length, scale, sub, toAngle } from '../../math.ts';
import {
  arcLengthTable,
  concatMovePaths,
  headingAtProgress,
  pathFromPolyline,
  pointAtProgress,
  restingArcTable,
  type CurveKind,
  type MovePath,
  type PathArcTable,
} from '../../path.ts';
import { advanceThroughTerrain, type TerrainRun } from '../../terrainRun.ts';
import type { BoatState, Vec2 } from '../../types.ts';

const EPSILON = 1e-9;
/** Share of the hull from the transom that counts as its back. */
const REAR_QUARTER = 0.25;
/** Within 60° of the struck boat's heading: the striker was following, not crossing. */
const SAME_WAY_COS = 0.5;
/**
 * Within 45° of the struck boat's heading: the blow pushes it forward, into its own way. A
 * diagonal brush past a stern corner pushes it sideways and is a side rub.
 */
const REAR_PUSH_COS = Math.SQRT1_2;

export type { FleetRun, FleetSeat };

export type FleetResult = {
  runs: FleetRun[];
  contacts: BoatContact[];
};

/** One uninterrupted stretch of a seat's turn. */
type Leg = {
  id: number;
  dimensions: HullDimensions;
  /** Turn fraction this leg starts at. */
  from: number;
  start: BoatState;
  path: MovePath | null;
  table: PathArcTable;
  /** Distance per turn on this leg, so the sweep can read a velocity from it. */
  rate: number;
  landed: BoatState;
  /**
   * A parked seat's way: the velocity of its last move. It does not travel this turn, but it is
   * a boat under way, and a hit is resolved against that, not against a stopped hull. Unset for a
   * seat that is moving, whose path is its velocity.
   */
  way?: Vec2;
};

/** A seat's turn as it is being assembled, leg by leg. */
type Total = {
  kind: CurveKind;
  pieces: MovePath[];
  /** Whether the open leg contributed a piece, so a trim knows what to pop. */
  openPiece: boolean;
  impactPath: MovePath | null;
  clipped: boolean;
  crash: CrashImpact | null;
  crossingDeg: number | null;
  contacts: CollisionContact[];
  reported: CollisionContact[];
  shoved: boolean;
};

type Pose = {
  capsule: HullCapsule;
  velocity: Vec2;
};

type SweepHit = {
  at: number;
  a: Leg;
  b: Leg;
  point: Vec2;
  normal: Vec2;
  poseA: Pose;
  poseB: Pose;
};

type Drift = {
  id: number;
  velocity: Vec2;
  yaw: number;
  /** Where the hull is when the boats meet. The picture starts here. */
  from: Vec2;
  /** Where the centres sit once they have been pushed clear. The coast is measured from here. */
  resolved: Vec2;
  heading: number;
  /** The seat's way before the hit, when it was parked. Its drift is only the change to that. */
  way?: Vec2;
};

/**
 * Resolve the turn for every seat at once. One seat is the same answer
 * `planMove` gives on its own, so the caller does not need to branch.
 *
 * The host calls this only when boat collisions are installed and this race
 * wants them on. Terrain for a race that does not is the host's own fallback.
 */
export function resolveFleetTurn(
  map: MapDocument,
  seats: readonly FleetSeat[],
  crashHarshness: number = CRASH_HARSHNESS,
  turnTax: number = TURN_TAX,
): FleetResult {
  const legs = new Map<number, Leg>();
  const totals = new Map<number, Total>();

  for (const seat of seats) {
    const opened = buildLeg(
      map,
      seat.id,
      seat.dimensions,
      0,
      seat.boat,
      seat.intended,
      seat.landedIfClear ?? seat.boat,
      crashHarshness,
      turnTax,
    );
    const total: Total = {
      kind: seat.intended?.kind ?? 'arc',
      pieces: [],
      openPiece: false,
      impactPath: opened.run?.impactPath ?? null,
      clipped: false,
      crash: null,
      crossingDeg: null,
      contacts: [],
      reported: [],
      shoved: false,
    };

    absorb(total, opened);

    if (!seat.intended) {
      opened.leg.way = fromAngle(seat.boat.heading, seat.boat.speed);
    }

    legs.set(seat.id, opened.leg);
    totals.set(seat.id, total);
  }

  const contacts = seats.length > 1 ? sweep(map, legs, totals, crashHarshness, turnTax) : [];

  return {
    runs: seats.map((seat) => {
      const total = totals.get(seat.id)!;
      const leg = legs.get(seat.id)!;
      const owed = severityOwed(contacts, seat.id);

      return {
        id: seat.id,
        boat: owed ? penalizeAtFault(seat.boat, leg.landed, owed, crashHarshness) : leg.landed,
        path: concatMovePaths(total.kind, total.pieces),
        impactPath: total.impactPath,
        clipped: total.clipped,
        crash: total.crash,
        crossingDeg: total.crossingDeg,
        contacts: total.contacts,
        reported: total.reported,
        shoved: total.shoved,
      };
    }),
    contacts,
  };
}

/** Worst hit this seat caused this turn: it closed the gap, or both did (a head-on). */
function severityOwed(contacts: readonly BoatContact[], id: number): BoatHitSeverity | null {
  let worst: BoatHitSeverity | null = null;

  for (const contact of contacts) {
    const involved = contact.fromId === id || contact.intoId === id;

    if (involved && (contact.faultId === id || contact.faultId === null)) {
      worst = worseSeverity(worst, contact.severity);
    }
  }

  return worst;
}

/** Find, spend, and re-cast, until the turn holds no more contacts. */
function sweep(
  map: MapDocument,
  legs: Map<number, Leg>,
  totals: Map<number, Total>,
  crashHarshness: number,
  turnTax: number,
): BoatContact[] {
  const contacts: BoatContact[] = [];
  let time = 0;

  for (let guard = 0; guard < BOAT_CONTACT_MAX; guard += 1) {
    const hit = firstContact([...legs.values()], time);

    if (!hit) {
      break;
    }

    const spent = spendContact(hit, crashHarshness);

    if (!spent) {
      break;
    }

    contacts.push(spent.contact);
    time = hit.at;

    for (const drift of spent.drifts) {
      const leg = legs.get(drift.id)!;
      const total = totals.get(drift.id)!;

      trimTo(total, leg, hit.at);

      const next = driftLeg(map, leg, drift, hit.at, crashHarshness, turnTax);

      total.shoved = true;
      absorb(total, next);
      legs.set(drift.id, next.leg);
    }
  }

  return contacts;
}

type OpenLeg = { leg: Leg; run: TerrainRun | null };

/** Take one stretch of travel through the map and turn it into a leg. */
function buildLeg(
  map: MapDocument,
  id: number,
  dimensions: HullDimensions,
  from: number,
  start: BoatState,
  intended: MovePath | null,
  landedIfClear: BoatState,
  crashHarshness: number,
  turnTax: number,
): OpenLeg {
  const run = intended
    ? advanceThroughTerrain(map, start, intended, landedIfClear, crashHarshness, turnTax)
    : null;

  if (!run) {
    return { leg: legOf(id, dimensions, from, start, null, start), run: null };
  }

  return {
    leg: legOf(id, dimensions, from, start, run.path, run.boat),
    run,
  };
}

function legOf(
  id: number,
  dimensions: HullDimensions,
  from: number,
  start: BoatState,
  path: MovePath | null,
  landed: BoatState,
): Leg {
  const table = path ? arcLengthTable(path) : restingArcTable(start.position);

  return {
    id,
    dimensions,
    from,
    start,
    path,
    table,
    rate: table.total / Math.max(1 - from, EPSILON),
    landed,
  };
}

/** Fold a leg's terrain outcome into the seat's running total. */
function absorb(total: Total, opened: OpenLeg): void {
  total.openPiece = opened.leg.path !== null;

  if (opened.leg.path) {
    total.pieces.push(opened.leg.path);
  }

  if (!opened.run) {
    return;
  }

  total.clipped = total.clipped || opened.run.clipped;
  total.crash = total.crash ?? opened.run.crash;
  total.crossingDeg = total.crossingDeg ?? opened.run.crossingDeg;
  total.contacts = [...total.contacts, ...opened.run.contacts];
  total.reported = [...total.reported, ...opened.run.reported];
}

/** Where a hull is, and how fast, at turn fraction `time`. */
function poseAt(leg: Leg, time: number): Pose {
  const span = 1 - leg.from;
  const local = span <= EPSILON ? 1 : clamp((time - leg.from) / span, 0, 1);
  const center = pointAtProgress(leg.table, local);
  const heading = headingAtProgress(leg.table, local) ?? leg.start.heading;

  return {
    capsule: hullCapsule(leg.dimensions, center, heading),
    velocity: fromAngle(heading, leg.rate),
  };
}

/**
 * Earliest instant in `[from, 1]` at which a pair of hulls is both touching and
 * closing, and has been clear of each other since `from`.
 *
 * A pair that is already inside each other at `from` is not a hit. That is a
 * boat placed overlapping its neighbour, or the two hulls still untangling from
 * the contact just spent. They have to separate and meet again. Same rule as a
 * boat that starts on land: it may leave, and the overlap it began with is not
 * a new collision.
 */
function firstContact(legs: readonly Leg[], from: number): SweepHit | null {
  const start = clamp(from, 0, 1);
  const step = (1 - start) / BOAT_SWEEP_STEPS;
  const seenClear = new Set<string>();

  for (let index = 0; index <= BOAT_SWEEP_STEPS; index += 1) {
    const time = start + step * index;
    const poses = legs.map((leg) => poseAt(leg, time));
    let best: SweepHit | null = null;

    for (let i = 0; i < legs.length; i += 1) {
      for (let j = i + 1; j < legs.length; j += 1) {
        const key = pairKey(legs[i]!.id, legs[j]!.id);
        const touching = hullContact(poses[i]!.capsule, poses[j]!.capsule) !== null;

        if (!touching) {
          seenClear.add(key);
          continue;
        }

        if (!seenClear.has(key)) {
          continue;
        }

        const hit = closingPair(legs[i]!, legs[j]!, poses[i]!, poses[j]!, time);

        if (hit && (!best || earlier(hit, best))) {
          best = hit;
        }
      }
    }

    if (best) {
      return refine(best, Math.max(start, time - step));
    }
  }

  return null;
}

function pairKey(a: number, b: number): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}

function closingPair(
  a: Leg,
  b: Leg,
  poseA: Pose,
  poseB: Pose,
  time: number,
): SweepHit | null {
  const contact = hullContact(poseA.capsule, poseB.capsule);

  if (!contact) {
    return null;
  }

  if (dot(sub(poseB.velocity, poseA.velocity), contact.normal) >= -EPSILON) {
    return null;
  }

  return { at: time, a, b, point: contact.point, normal: contact.normal, poseA, poseB };
}

/** Same instant: fall back to seat ids, so the order is the same on every run. */
function earlier(candidate: SweepHit, best: SweepHit): boolean {
  if (candidate.at !== best.at) {
    return candidate.at < best.at;
  }

  const low = Math.min(candidate.a.id, candidate.b.id);
  const bestLow = Math.min(best.a.id, best.b.id);

  if (low !== bestLow) {
    return low < bestLow;
  }

  return Math.max(candidate.a.id, candidate.b.id) < Math.max(best.a.id, best.b.id);
}

/** Walk the contact back to where the hulls first meet. Fixed iterations, so replay agrees. */
function refine(hit: SweepHit, clearAt: number): SweepHit {
  let clear = clearAt;
  let touching = hit.at;
  let found = hit;

  for (let step = 0; step < BOAT_SWEEP_REFINEMENTS; step += 1) {
    const middle = (clear + touching) / 2;
    const candidate = closingPair(
      hit.a,
      hit.b,
      poseAt(hit.a, middle),
      poseAt(hit.b, middle),
      middle,
    );

    if (candidate) {
      found = candidate;
      touching = middle;
    } else {
      clear = middle;
    }
  }

  return found;
}

/** Run the impulse, push the hulls apart, and turn that into a drift for each boat. */
function spendContact(
  hit: SweepHit,
  crashHarshness: number,
): { contact: BoatContact; drifts: Drift[] } | null {
  // Each hull at its way, so the impulse reads the same closing speed the severity does: two
  // boats at the same speed rubbing sides trade almost nothing.
  const wayA = wayVelocity(hit.a, hit.poseA);
  const wayB = wayVelocity(hit.b, hit.poseB);
  const bodyA = impactBody(hit.a.dimensions, hit.poseA.capsule, wayA);
  const bodyB = impactBody(hit.b.dimensions, hit.poseB.capsule, wayB);
  // The hulls met closing on the water, but at their ways they may already be parting (a boat
  // brushing a faster one). No impulse then; they are still pushed apart below.
  const impact = resolveBoatImpact(
    bodyA,
    bodyB,
    hit.point,
    hit.normal,
    1 - hit.at,
    crashHarshness,
  ) ?? {
    a: { velocity: wayA, yaw: 0 },
    b: { velocity: wayB, yaw: 0 },
    impulse: 0,
    closingSpeed: 0,
  };

  const parted = separateAfterImpact(
    bodyA,
    bodyB,
    hit.normal,
    hullContact(hit.poseA.capsule, hit.poseB.capsule)?.depth ?? 0,
    impact.a,
    impact.b,
    1 - hit.at,
    impact.closingSpeed,
    crashHarshness,
  );

  const fromAlong = contactAlongHull(hit.poseA.capsule, hit.point);
  const intoAlong = contactAlongHull(hit.poseB.capsule, hit.point);
  const faultId = faultFromClosing(
    hit.a.id,
    hit.b.id,
    hit.poseA.velocity,
    hit.poseB.velocity,
    hit.normal,
  );
  const rear =
    faultId === hit.a.id
      ? fromBehind(wayVelocity(hit.a, hit.poseA), hit.poseB.capsule.heading, intoAlong, hit.normal)
      : faultId === hit.b.id
        ? fromBehind(
            wayVelocity(hit.b, hit.poseB),
            hit.poseA.capsule.heading,
            fromAlong,
            scale(hit.normal, -1),
          )
        : false;
  const severity = classifyBoatHit(wayClosing(hit.a, hit.poseA, hit.b, hit.poseB, hit.normal));

  return {
    contact: {
      fromId: hit.a.id,
      intoId: hit.b.id,
      point: { ...hit.point },
      normal: { ...hit.normal },
      at: hit.at,
      impulse: impact.impulse,
      closingSpeed: impact.closingSpeed,
      fromAlong,
      intoAlong,
      faultId,
      rearEnd: rear,
      // Running into someone's back is braking too late: never free, whatever the speed.
      severity: rear ? worseSeverity(severity, 'medium') : severity,
    },
    drifts: [
      driftOf(hit.a.id, parted.a, hit.poseA.capsule.center, hit.poseA.capsule.heading, hit.a.way),
      driftOf(hit.b.id, parted.b, hit.poseB.capsule.center, hit.poseB.capsule.heading, hit.b.way),
    ],
  };
}

/**
 * A parked hull carries no velocity in the sweep, but it is still a boat under way: in
 * Sequential every other seat is parked mid-race at the speed of its last move. Severity reads
 * that way, so two boats at the same speed rubbing sides is a nudge, not a ram of a stopped boat.
 */
function wayVelocity(leg: Leg, pose: Pose): Vec2 {
  return leg.way ?? pose.velocity;
}

/**
 * Running into someone's back: a blow on the struck hull's rear quarter, from a boat heading
 * roughly the same way, that pushes the struck boat forward along its own heading (`push` is the
 * unit normal from striker to struck). The boat behind braked too late. Side-by-side rubs,
 * diagonal brushes past a stern corner, and crossing hits are not this.
 */
function fromBehind(
  strikerWay: Vec2,
  struckHeading: number,
  struckAlong: number,
  push: Vec2,
): boolean {
  const speed = length(strikerWay);

  if (struckAlong >= REAR_QUARTER || speed < EPSILON) {
    return false;
  }

  const ahead = fromAngle(struckHeading);
  return dot(strikerWay, ahead) / speed >= SAME_WAY_COS && dot(push, ahead) >= REAR_PUSH_COS;
}

/** How fast the hulls were closing along `normal` (a toward b), each at its way. */
function wayClosing(a: Leg, poseA: Pose, b: Leg, poseB: Pose, normal: Vec2): number {
  return Math.max(0, dot(sub(wayVelocity(a, poseA), wayVelocity(b, poseB)), normal));
}

function driftOf(
  id: number,
  change: { velocity: Vec2; yaw: number; center: Vec2 },
  from: Vec2,
  heading: number,
  way?: Vec2,
): Drift {
  return {
    id,
    velocity: change.velocity,
    yaw: change.yaw,
    from: { ...from },
    resolved: change.center,
    heading,
    way,
  };
}

/**
 * What a boat does with the rest of its turn after being hit: it carries the
 * velocity it was left with, in a straight line. It does not resume the arc it
 * planned, because nobody steered it there, and it still meets the shore on the
 * same terms as any other travel. The yaw from the impulse lands on the heading
 * it finishes with, so the next turn's oval is drawn on the spun hull.
 */
function driftLeg(
  map: MapDocument,
  leg: Leg,
  drift: Drift,
  at: number,
  crashHarshness: number,
  turnTax: number,
): OpenLeg {
  const speed = length(drift.velocity);
  const heading = speed > EPSILON ? toAngle(drift.velocity) : drift.heading;
  const start: BoatState = {
    position: { ...drift.from },
    heading,
    speed,
    drive: driveLevel(leg.start),
  };
  // A parked boat keeps its way and only takes the change: it slides by the push, not by the
  // speed it was already carrying, and lands on that way turned by the push. A nudge between
  // boats at the same speed barely moves it. A boat that was moving slides along its new
  // velocity for the rest of the turn.
  const slide = drift.way ? sub(drift.velocity, drift.way) : drift.velocity;
  // The separation is part of the slide, not a jump to a new centre before it.
  const end = add(drift.resolved, scale(slide, Math.max(1 - at, 0)));
  const landedSpeed = drift.way ? speed : MIN_SPEED;
  const way = drift.way ? fromAngle(heading + drift.yaw, speed) : undefined;

  if (length(sub(end, drift.from)) <= EPSILON) {
    const parked = legOf(leg.id, leg.dimensions, at, start, null, {
      ...start,
      position: { ...end },
      speed: landedSpeed,
      heading: heading + drift.yaw,
    });
    parked.way = way;
    return { leg: parked, run: null };
  }

  const opened = buildLeg(
    map,
    leg.id,
    leg.dimensions,
    at,
    start,
    pathFromPolyline([drift.from, end], 'straight'),
    { position: end, heading, speed, drive: driveLevel(leg.start) },
    crashHarshness,
    turnTax,
  );

  return {
    ...opened,
    leg: {
      ...opened.leg,
      landed: { ...opened.leg.landed, heading: opened.leg.landed.heading + drift.yaw },
      way,
    },
  };
}

/** Cut a seat's accumulated path back to the instant it was hit. */
function trimTo(total: Total, leg: Leg, at: number): void {
  if (!total.openPiece) {
    return;
  }

  total.pieces.pop();
  total.openPiece = false;

  const span = 1 - leg.from;
  const local = span <= EPSILON ? 1 : clamp((at - leg.from) / span, 0, 1);

  if (!leg.path || local <= EPSILON) {
    return;
  }

  const kept = keepPrefix(leg, local);

  if (kept) {
    total.pieces.push(kept);
  }
}

/** The part of a leg's path swum before `local`, flattened into its own path. */
function keepPrefix(leg: Leg, local: number): MovePath | null {
  const steps = Math.max(2, Math.ceil(BOAT_SWEEP_STEPS * local));
  const points: Vec2[] = [];

  for (let step = 0; step <= steps; step += 1) {
    points.push(pointAtProgress(leg.table, (local * step) / steps));
  }

  return pathFromPolyline(points, leg.path?.kind ?? 'straight');
}
